import { memo, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useModalContext } from 'components/interface/Modal/provider/ModalProvider'
import ModalBrandView from './modal/ModalBrandView/ModalBrandView'
import ModalLearnCompatibility from './modal/ModalLearnCompatibility/ModalLearnCompatibility'
import ModalLearnUtility from './modal/ModalLearnUtility/ModalLearnUtility'
import ModalMarketplaceCartFull from './modal/ModalMarketplaceCartFull/ModalMarketplaceCartFull'
import ModalMarketplaceCheckout from './modal/ModalMarketplaceCheckout/ModalMarketplaceCheckout'
import ModalMarketplaceLocked from './modal/ModalMarketplaceLocked/ModalMarketplaceLocked'
import ModalNewsletter from './modal/ModalNewsletter/ModalNewsletter'
import ModalPrivacyTerms from './modal/ModalPrivacyTerms/ModalPrivacyTerms'
import ModalSignIn from './modal/ModalSignIn/ModalSignIn'
import ModalSignedOut from './modal/ModalSignedOut/ModalSignedOut'
import ModalUtilityTerms from './modal/ModalUtilityTerms/ModalUtilityTerms'

/**
 * The `AppModals` houses the modal types used
 * by the main applications
 *
 * @returns {React.ReactElement?} the output
 */
function AppModals () {
  const { modalId, modalRef, props } = useModalContext()

  /**
   * Memo: The modal output. Utilizes portals to render
   * modals with the current context scope into the modal
   * container
   */
  const output = useMemo(() => {
    if (!modalRef.current) {
      return null
    }

    if (modalId === 'app:sign_in') {
      return createPortal(<ModalSignIn {...props} />, modalRef.current)
    }

    if (modalId === 'app:sign_up') {
      return createPortal(
        <ModalSignIn {...props} isSignUp={true} />,
        modalRef.current
      )
    }

    if (modalId === 'app:signed_out') {
      return createPortal(<ModalSignedOut {...props} />, modalRef.current)
    }

    if (modalId === 'app:privacy_terms') {
      return createPortal(<ModalPrivacyTerms {...props} />, modalRef.current)
    }

    if (modalId === 'app:utility_terms') {
      return createPortal(<ModalUtilityTerms {...props} />, modalRef.current)
    }

    if (modalId === 'app:newsletter') {
      return createPortal(<ModalNewsletter {...props} />, modalRef.current)
    }

    if (modalId === 'app:marketplace:locked') {
      return createPortal(
        <ModalMarketplaceLocked {...props} />,
        modalRef.current
      )
    }

    if (modalId === 'marketplace:cart:full') {
      return createPortal(
        <ModalMarketplaceCartFull {...props} />,
        modalRef.current
      )
    }

    if (modalId === 'marketplace:checkout') {
      return createPortal(
        <ModalMarketplaceCheckout {...props} />,
        modalRef.current
      )
    }

    if (modalId === 'brand:info') {
      return createPortal(<ModalBrandView {...props} />, modalRef.current)
    }

    if (modalId === 'learn:compatibility') {
      return createPortal(
        <ModalLearnCompatibility {...props} />,
        modalRef.current
      )
    }

    if (modalId === 'learn:utility') {
      return createPortal(<ModalLearnUtility {...props} />, modalRef.current)
    }
  }, [modalId, props])

  return output
}

// Memoize
export default memo(AppModals)
